/**
 * Services that authenticate to Nexus, and the lifecycle of their credentials
 * (`NXP-P8-SERVICE-LIFECYCLE-001`).
 *
 * Six operations, each decided separately by the platform and each asked about
 * separately here: reading, registering and revoking a service identity, and
 * issuing, rotating and revoking its credential. All six belong to the service
 * identity administrator, an authority the root of trust appoints — and none of
 * them is authority over what a service may *do*. That is a role, assigned on
 * Authorization by an authorization administrator, and this screen says so
 * wherever the difference could be missed.
 *
 * **The secret.** Issue and rotate are the only two responses that ever carry
 * one, and the platform keeps only a hash. So this screen is the one place the
 * secret exists in the browser, and it is held to exactly that:
 *
 * - in component state, and nowhere else — never storage, the URL, a log, an
 *   error message or an outcome line;
 * - shown once, in a panel the operator dismisses, and gone on dismissal or
 *   when the screen unmounts;
 * - never rendered in a list or a detail view, which have nothing to render:
 *   the reads do not return it.
 *
 * And both requests go out without an `Idempotency-Key`. The platform refuses
 * one on these routes, because replaying a response would mean storing the
 * secret to replay; a retry is refused rather than answered with a second one.
 */

import { useCallback, useState } from "react";
import { api } from "../api/client";
import type {
  IssuedServiceCredential,
  Page,
  ServiceCredential,
  ServiceIdentity,
  ServiceIdentityKind,
} from "../api/types";
import { usePagedCollection } from "../api/useCollection";
import { change, Result, type Outcome } from "../components/governed";
import {
  Banner,
  Card,
  Fact,
  Facts,
  Identifier,
  Mono,
  More,
  Pill,
  when,
  type Tone,
} from "../components/primitives";
import { Empty, Forbidden, Show } from "../components/states";
import { useSession } from "../session/SessionProvider";

/**
 * The capability keys the decision point is asked about, one per operation.
 *
 * Each is offered on its own. Holding `read` is not evidence of `register`, and
 * holding `issue` is not evidence of `rotate` — the platform decides them apart.
 */
const READ = "service_identity.read";
const REGISTER = "service_identity.register";
const REVOKE_IDENTITY = "service_identity.revoke";
const ISSUE = "service_credential.issue";
const ROTATE = "service_credential.rotate";
const REVOKE_CREDENTIAL = "service_credential.revoke";

const KINDS: { value: ServiceIdentityKind; label: string }[] = [
  { value: "product_adapter", label: "Product adapter" },
  { value: "external_integration", label: "External integration" },
  { value: "agent_runtime", label: "Agent runtime" },
];

/** How long a rotated-out credential keeps working. The platform allows 0 to 24 hours. */
const OVERLAPS: { seconds: number; label: string }[] = [
  { seconds: 0, label: "End the current credential now" },
  { seconds: 3600, label: "Keep the current credential working for 1 hour" },
  { seconds: 86400, label: "Keep the current credential working for 24 hours" },
];

const CREDENTIAL_TONE: Record<string, Tone> = {
  active: "ok",
  retiring: "wait",
  expired: "quiet",
  revoked: "quiet",
};

function kindLabel(kind: string): string {
  return KINDS.find((candidate) => candidate.value === kind)?.label ?? kind.replace(/_/g, " ");
}

/** A credential that still authenticates: current, or rotated out and in its overlap. */
function isLive(credential: ServiceCredential): boolean {
  return credential.state === "active" || credential.state === "retiring";
}

function isRevoked(identity: ServiceIdentity): boolean {
  return identity.status === "deprovisioned";
}

/**
 * The secret, and what it was issued for — held by the page, never persisted.
 *
 * At the page rather than in the panel that asked for it, because a change
 * refreshes the list and the panel unmounts while it does. Losing the secret at
 * that moment would lose it for good.
 */
interface Revealed {
  serviceName: string;
  label: string;
  serviceCredentialId: string;
  credential: string;
}

// ── the secret, once ──────────────────────────────────────────────────────

function SecretPanel({ revealed, onDismiss }: { revealed: Revealed; onDismiss: () => void }) {
  const [copied, setCopied] = useState<"idle" | "copied" | "failed">("idle");

  return (
    <section
      className="nx-banner nx-banner--wait"
      aria-labelledby="secret-title"
      style={{ display: "block" }}
    >
      <p className="nx-banner__title" id="secret-title">
        Store this now in the adopter&apos;s backend secret manager. It will not be shown again.
      </p>
      <div className="nx-banner__body">
        <p style={{ margin: "0 0 8px" }}>
          The credential <strong>{revealed.label}</strong> for{" "}
          <strong>{revealed.serviceName}</strong>. Nexus keeps only a hash of it, so nobody —
          including you — can read it back after this panel is dismissed. Losing it means rotating.
        </p>
        <code
          className="nx-mono"
          aria-label="Credential secret"
          style={{
            display: "block",
            padding: "8px 10px",
            wordBreak: "break-all",
            userSelect: "all",
            background: "var(--nx-surface)",
            border: "1px solid var(--nx-border-firm)",
            borderRadius: "var(--nx-radius-sm)",
          }}
        >
          {revealed.credential}
        </code>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            className="nx-button"
            onClick={() => {
              void (async () => {
                try {
                  await navigator.clipboard.writeText(revealed.credential);
                  setCopied("copied");
                } catch {
                  // Said without the reason: a clipboard error is nothing the
                  // operator can act on, and nothing here repeats the secret.
                  setCopied("failed");
                }
              })();
            }}
          >
            Copy to clipboard
          </button>
          <button type="button" className="nx-button nx-button--primary" onClick={onDismiss}>
            I have stored it — dismiss
          </button>
        </div>
        {copied === "copied" ? (
          <p role="status" className="nx-hint" style={{ marginTop: 8 }}>
            Copied. Paste it into the secret manager, then clear your clipboard.
          </p>
        ) : copied === "failed" ? (
          <p role="status" className="nx-hint" style={{ marginTop: 8 }}>
            This browser would not copy it. Select the value above and copy it by hand.
          </p>
        ) : null}
      </div>
    </section>
  );
}

// ── registering ───────────────────────────────────────────────────────────

function Register({
  onRegistered,
  onOutcome,
}: {
  onRegistered: (identity: ServiceIdentity) => void;
  onOutcome: (outcome: Outcome) => void;
}) {
  const [form, setForm] = useState({
    key: "",
    displayName: "",
    owner: "",
    kind: "product_adapter" as ServiceIdentityKind,
  });
  const [busy, setBusy] = useState(false);

  return (
    <Card title="Register a service">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          const registered: { value?: ServiceIdentity } = {};
          void change(
            async (correlationId) => {
              // Exactly the fields the API declares; it forbids every other,
              // including any that would name who acted. A key is safe here —
              // the response carries no secret — so a retry replays.
              registered.value = await api.post<ServiceIdentity>(
                "/service-identities",
                {
                  key: form.key,
                  display_name: form.displayName,
                  owner: form.owner,
                  kind: form.kind,
                },
                { correlationId, idempotent: true },
              );
            },
            "Registering a service",
            `Service ${form.key} registered. It has no credential and no authority yet.`,
          )
            .then((result) => {
              onOutcome(result);
              if (result.tone === "ok") {
                setForm({ key: "", displayName: "", owner: "", kind: "product_adapter" });
                if (registered.value !== undefined) onRegistered(registered.value);
              }
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        <div className="nx-field">
          <label htmlFor="service-key">Key</label>
          <input
            id="service-key"
            className="nx-input nx-mono"
            required
            minLength={2}
            maxLength={64}
            pattern="[a-z][a-z0-9_.\-]{1,63}"
            aria-describedby="service-key-hint"
            value={form.key}
            onChange={(event) => {
              setForm({ ...form, key: event.target.value });
            }}
          />
          <span className="nx-hint" id="service-key-hint">
            Unique in this tenant: lowercase letters, digits, &apos;_&apos;, &apos;.&apos; or
            &apos;-&apos;, starting with a letter.
          </span>
        </div>
        <div className="nx-field">
          <label htmlFor="service-name">Display name</label>
          <input
            id="service-name"
            className="nx-input"
            required
            maxLength={256}
            value={form.displayName}
            onChange={(event) => {
              setForm({ ...form, displayName: event.target.value });
            }}
          />
        </div>
        <div className="nx-field">
          <label htmlFor="service-owner">Accountable owner</label>
          <input
            id="service-owner"
            className="nx-input"
            required
            maxLength={256}
            value={form.owner}
            onChange={(event) => {
              setForm({ ...form, owner: event.target.value });
            }}
          />
        </div>
        <div className="nx-field">
          <label htmlFor="service-kind">Kind</label>
          <select
            id="service-kind"
            className="nx-select"
            value={form.kind}
            onChange={(event) => {
              setForm({ ...form, kind: event.target.value as ServiceIdentityKind });
            }}
          >
            {KINDS.map((kind) => (
              <option key={kind.value} value={kind.value}>
                {kind.label}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
          Register
        </button>
        <p className="nx-hint" style={{ marginTop: 8 }}>
          Registering creates an identity and nothing else: no credential, and no authority. What
          this service may do is a role, assigned separately by an authorization administrator on
          Authorization.
        </p>
      </form>
    </Card>
  );
}

// ── one service ───────────────────────────────────────────────────────────

/**
 * Ask for a secret-bearing credential and hand it to the page.
 *
 * No `idempotent` here, and that is the contract rather than an omission: the
 * platform refuses an `Idempotency-Key` on both routes, because the only way to
 * replay the response would be to store the secret in order to replay it.
 */
async function obtain(
  path: string,
  body: Record<string, unknown>,
  whatWasRefused: string,
  succeeded: string,
): Promise<{ outcome: Outcome; issued: IssuedServiceCredential | null }> {
  const received: { value?: IssuedServiceCredential } = {};
  const outcome = await change(
    async (correlationId) => {
      received.value = await api.post<IssuedServiceCredential>(path, body, { correlationId });
    },
    whatWasRefused,
    succeeded,
  );
  return { outcome, issued: outcome.tone === "ok" ? (received.value ?? null) : null };
}

function IssueFirst({
  identity,
  onIssued,
}: {
  identity: ServiceIdentity;
  onIssued: (outcome: Outcome, issued: IssuedServiceCredential | null) => void;
}) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form
      style={{ padding: "0 18px 18px" }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void obtain(
          `/service-identities/${identity.service_identity_id}/credentials`,
          { label },
          "Issuing a credential",
          "Credential issued. Its secret is shown above, once.",
        )
          .then(({ outcome, issued }) => {
            if (outcome.tone === "ok") setLabel("");
            onIssued(outcome, issued);
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>Issue its first credential</h3>
      <div className="nx-field">
        <label htmlFor="issue-label">Credential label</label>
        <input
          id="issue-label"
          className="nx-input"
          required
          maxLength={64}
          value={label}
          onChange={(event) => {
            setLabel(event.target.value);
          }}
        />
      </div>
      <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
        Issue credential
      </button>
    </form>
  );
}

function Rotate({
  identity,
  credential,
  onIssued,
  onCancel,
}: {
  identity: ServiceIdentity;
  credential: ServiceCredential;
  onIssued: (outcome: Outcome, issued: IssuedServiceCredential | null) => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState("");
  const [overlap, setOverlap] = useState(0);
  const [busy, setBusy] = useState(false);
  return (
    <form
      style={{ padding: "0 18px 18px" }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void obtain(
          `/service-identities/${identity.service_identity_id}/credentials/${credential.service_credential_id}/rotation`,
          { label, overlap_seconds: overlap },
          "Rotating a credential",
          overlap === 0
            ? `Rotated. ${credential.label} no longer works; the new secret is shown above, once.`
            : `Rotated. ${credential.label} keeps working until its overlap ends; the new secret is shown above, once.`,
        )
          .then(({ outcome, issued }) => {
            onIssued(outcome, issued);
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <h3 style={{ fontSize: "var(--nx-text-base)", margin: "0 0 10px" }}>
        Rotate {credential.label}
      </h3>
      <div className="nx-field">
        <label htmlFor="rotate-label">New credential label</label>
        <input
          id="rotate-label"
          className="nx-input"
          required
          maxLength={64}
          value={label}
          onChange={(event) => {
            setLabel(event.target.value);
          }}
        />
      </div>
      <div className="nx-field">
        <label htmlFor="rotate-overlap">Overlap</label>
        <select
          id="rotate-overlap"
          className="nx-select"
          value={overlap}
          onChange={(event) => {
            setOverlap(Number(event.target.value));
          }}
        >
          {OVERLAPS.map((choice) => (
            <option key={choice.seconds} value={choice.seconds}>
              {choice.label}
            </option>
          ))}
        </select>
        <span className="nx-hint">
          An overlap lets the service switch to the new secret before the old one stops working.
          The same identity keeps every role it holds either way.
        </span>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" className="nx-button nx-button--primary" disabled={busy}>
          Rotate
        </button>
        <button type="button" className="nx-button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function RevokeIdentity({
  identity,
  onOutcome,
  onChanged,
}: {
  identity: ServiceIdentity;
  onOutcome: (outcome: Outcome) => void;
  onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!confirming) {
    return (
      <div style={{ padding: "0 18px 18px" }}>
        <button
          type="button"
          className="nx-button"
          onClick={() => {
            setConfirming(true);
          }}
        >
          Revoke this service identity
        </button>
      </div>
    );
  }

  return (
    <div role="group" aria-labelledby="revoke-identity-title" style={{ padding: "0 18px 18px" }}>
      <Banner tone="bad" title={`Revoking ${identity.display_name} is permanent.`}>
        <p style={{ margin: 0 }} id="revoke-identity-title">
          It ends every credential and token this service holds, now, and it cannot be undone — the
          identity can never authenticate again. Its role assignments are not ended by this: an
          authorization administrator must end them separately, on Authorization.
        </p>
      </Banner>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button
          type="button"
          className="nx-button nx-button--primary"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void change(
              (correlationId) =>
                api.post<ServiceIdentity>(
                  `/service-identities/${identity.service_identity_id}/revocation`,
                  {},
                  { correlationId, idempotent: true },
                ),
              "Revoking a service identity",
              `${identity.display_name} is revoked for good. End its role assignments on Authorization.`,
            )
              .then((result) => {
                onOutcome(result);
                setConfirming(false);
                if (result.tone === "ok") onChanged();
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        >
          Revoke permanently
        </button>
        <button
          type="button"
          className="nx-button"
          onClick={() => {
            setConfirming(false);
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Detail({
  identity,
  onRevealed,
  onOutcome,
  onChanged,
}: {
  identity: ServiceIdentity;
  onRevealed: (revealed: Revealed) => void;
  onOutcome: (outcome: Outcome) => void;
  onChanged: () => void;
}) {
  const session = useSession();
  const mayIssue = session.may(ISSUE);
  const mayRotate = session.may(ROTATE);
  const mayRevokeCredential = session.may(REVOKE_CREDENTIAL);
  const mayRevokeIdentity = session.may(REVOKE_IDENTITY);
  const [rotating, setRotating] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const revoked = isRevoked(identity);
  const live = identity.credentials.filter(isLive);
  const credentialActions = !revoked && (mayRotate || mayRevokeCredential);
  const rotatingCredential = identity.credentials.find(
    (credential) => credential.service_credential_id === rotating,
  );

  const received = (outcome: Outcome, issued: IssuedServiceCredential | null) => {
    onOutcome(outcome);
    if (issued !== null) {
      onRevealed({
        serviceName: identity.display_name,
        label: issued.label,
        serviceCredentialId: issued.service_credential_id,
        credential: issued.credential,
      });
      setRotating(null);
      onChanged();
    }
  };

  return (
    <Card title={identity.display_name} bodyless>
      <div className="nx-card__body">
        <Facts>
          <Fact label="Key">
            <Mono>{identity.key}</Mono>
          </Fact>
          <Fact label="Kind">{kindLabel(identity.kind)}</Fact>
          <Fact label="Accountable owner">{identity.owner}</Fact>
          <Fact label="Status">
            <Pill tone={revoked ? "bad" : "ok"}>{revoked ? "revoked" : identity.status}</Pill>
          </Fact>
          <Fact label="Registered">{when(identity.created_at)}</Fact>
          <Fact label="Identifier">
            <Identifier value={identity.service_identity_id} />
          </Fact>
        </Facts>
        <p className="nx-hint" style={{ marginTop: 10 }}>
          {revoked
            ? "Revoked, permanently. This identity cannot authenticate again. Any role assignments it still holds are ended on Authorization, by an authorization administrator."
            : "What this service may do is not decided here. It holds whatever roles an authorization administrator has assigned it on Authorization, and nothing else."}
        </p>
      </div>

      {identity.credentials.length === 0 ? (
        <Empty title="No credential has been issued." hint="Without one, this service cannot authenticate." />
      ) : (
        <div className="nx-table-wrap">
          <table className="nx-table">
            <caption className="nx-visually-hidden">Credentials of {identity.display_name}</caption>
            <thead>
              <tr>
                <th scope="col">Credential</th>
                <th scope="col">State</th>
                <th scope="col">Issued</th>
                <th scope="col">Stops working</th>
                <th scope="col">Revoked</th>
                {credentialActions ? <th scope="col">Change</th> : null}
              </tr>
            </thead>
            <tbody>
              {identity.credentials.map((credential) => (
                <tr key={credential.service_credential_id}>
                  <td>
                    <span className="nx-table__primary">{credential.label}</span>
                    <span className="nx-table__secondary" style={{ display: "block" }}>
                      <Identifier value={credential.service_credential_id} />
                    </span>
                  </td>
                  <td>
                    <Pill tone={CREDENTIAL_TONE[credential.state] ?? "quiet"}>{credential.state}</Pill>
                  </td>
                  <td>{when(credential.created_at)}</td>
                  <td>{when(credential.expires_at)}</td>
                  <td>{when(credential.revoked_at)}</td>
                  {credentialActions ? (
                    <td>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {/* Only the current credential rotates; one already
                            rotating out is replaced by rotating its successor. */}
                        {mayRotate && credential.state === "active" ? (
                          <button
                            type="button"
                            className="nx-button"
                            aria-pressed={rotating === credential.service_credential_id}
                            onClick={() => {
                              setRotating(credential.service_credential_id);
                            }}
                          >
                            Rotate
                          </button>
                        ) : null}
                        {mayRevokeCredential && isLive(credential) ? (
                          <button
                            type="button"
                            className="nx-button"
                            disabled={busy === credential.service_credential_id}
                            onClick={() => {
                              setBusy(credential.service_credential_id);
                              void change(
                                (correlationId) =>
                                  api.post<ServiceIdentity>(
                                    `/service-identities/${identity.service_identity_id}/credentials/${credential.service_credential_id}/revocation`,
                                    {},
                                    { correlationId, idempotent: true },
                                  ),
                                "Revoking a credential",
                                `${credential.label} revoked. It, and every token it issued, stopped working now.`,
                              )
                                .then((result) => {
                                  onOutcome(result);
                                  if (result.tone === "ok") onChanged();
                                })
                                .finally(() => {
                                  setBusy(null);
                                });
                            }}
                          >
                            Revoke credential
                          </button>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!revoked && rotatingCredential !== undefined && mayRotate ? (
        <Rotate
          key={rotatingCredential.service_credential_id}
          identity={identity}
          credential={rotatingCredential}
          onIssued={received}
          onCancel={() => {
            setRotating(null);
          }}
        />
      ) : null}

      {!revoked && mayIssue ? (
        live.length === 0 ? (
          <IssueFirst identity={identity} onIssued={received} />
        ) : (
          <p className="nx-hint" style={{ padding: "0 18px 12px" }}>
            This service already has a live credential, so a new one is made by rotating it.
          </p>
        )
      ) : null}

      {!revoked && mayRevokeIdentity ? (
        <RevokeIdentity
          key={identity.service_identity_id}
          identity={identity}
          onOutcome={onOutcome}
          onChanged={onChanged}
        />
      ) : null}
    </Card>
  );
}

// ── the page ──────────────────────────────────────────────────────────────

export function ServiceIdentities() {
  const session = useSession();
  const mayRead = session.may(READ);
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<ServiceIdentity>>("/service-identities", {
        params: { limit: 50, cursor },
        signal,
      }),
    [],
  );
  const identities = usePagedCollection<ServiceIdentity>(readPage, { enabled: mayRead });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  // The secret. Component state only, so it goes when the panel is dismissed
  // or the screen unmounts, and it is never handed to anything that persists.
  const [revealed, setRevealed] = useState<Revealed | null>(null);

  const selected =
    identities.items.find((identity) => identity.service_identity_id === selectedId) ?? null;

  return (
    <>
      <Banner title="Registering a service grants it no authority.">
        A service identity is something that can authenticate, and nothing more. What it may do is
        a role, assigned separately by an authorization administrator on Authorization — a
        different authority from the one that manages services here.
      </Banner>
      <div style={{ display: "grid", gap: 18 }}>
        {revealed !== null ? (
          <SecretPanel
            revealed={revealed}
            onDismiss={() => {
              setRevealed(null);
            }}
          />
        ) : null}
        {outcome !== null ? (
          <div>
            <Result outcome={outcome} />
          </div>
        ) : null}

        {mayRead ? (
          <Card
            title="Service identities"
            actions={
              <button type="button" className="nx-button" onClick={identities.refresh}>
                Refresh
              </button>
            }
            bodyless
          >
            <Show
              resource={identities.state}
              what="the service identities in this tenant"
              onRetry={identities.refresh}
              empty={() =>
                identities.items.length === 0 ? (
                  <Empty
                    title="No service identities are registered."
                    hint="A service cannot authenticate to Nexus until one is registered and given a credential."
                  />
                ) : null
              }
            >
              {() => (
                <div className="nx-table-wrap">
                  <table className="nx-table">
                    <caption className="nx-visually-hidden">Service identities in this tenant</caption>
                    <thead>
                      <tr>
                        <th scope="col">Service</th>
                        <th scope="col">Kind</th>
                        <th scope="col">Owner</th>
                        <th scope="col">Status</th>
                        <th scope="col">Live credentials</th>
                        <th scope="col">Registered</th>
                        <th scope="col">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {identities.items.map((identity) => (
                        <tr key={identity.service_identity_id}>
                          <td>
                            <span className="nx-table__primary">{identity.display_name}</span>
                            <span className="nx-table__secondary" style={{ display: "block" }}>
                              <Mono>{identity.key}</Mono>
                            </span>
                          </td>
                          <td>{kindLabel(identity.kind)}</td>
                          <td>{identity.owner}</td>
                          <td>
                            <Pill tone={isRevoked(identity) ? "bad" : "ok"}>
                              {isRevoked(identity) ? "revoked" : identity.status}
                            </Pill>
                          </td>
                          <td>{identity.credentials.filter(isLive).length}</td>
                          <td>{when(identity.created_at)}</td>
                          <td>
                            <button
                              type="button"
                              className="nx-button"
                              aria-pressed={selectedId === identity.service_identity_id}
                              aria-label={`Show ${identity.display_name}`}
                              onClick={() => {
                                setSelectedId(identity.service_identity_id);
                              }}
                            >
                              {selectedId === identity.service_identity_id ? "Showing" : "Show"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Show>
            <More
              collection={identities}
              label="Show more service identities"
              hint="This is one page of the service identities registered here, not all of them."
              placement="bodyless"
            />
          </Card>
        ) : (
          <Card title="Service identities">
            <Forbidden what="the service identities in this tenant" />
          </Card>
        )}

        {selected !== null ? (
          <Detail
            identity={selected}
            onRevealed={setRevealed}
            onOutcome={setOutcome}
            onChanged={identities.refresh}
          />
        ) : null}

        {session.may(REGISTER) ? (
          <Register
            onOutcome={setOutcome}
            onRegistered={(identity) => {
              setSelectedId(identity.service_identity_id);
              identities.refresh();
            }}
          />
        ) : null}
      </div>
    </>
  );
}
